import { inject, Injectable, OnDestroy, signal } from '@angular/core';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  User as FirebaseUser,
  Unsubscribe,
  sendPasswordResetEmail,
  confirmPasswordReset
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable, HttpsCallableResult } from 'firebase/functions';
import { User } from '../models/user.model';
import { FirebaseService } from './firebase.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  user = signal<User | null>(null);

  firebaseService = inject(FirebaseService);
  auth = this.firebaseService.auth;
  db = this.firebaseService.db;

  unsubscribe: Unsubscribe | null = null;

  constructor() {
    this.unsubscribe = onAuthStateChanged(
      this.firebaseService.auth,
      async (user) => {
        if (!user) {
          this.user.set(null);
          return;
        } else {
          const userInfo = await this.getUserInfo(user.uid);
          this.user.set(userInfo);
          // Claims are available for role-based access control
        }
      }
    );
  }

  async getAuthState(): Promise<FirebaseUser | null> {
    await this.auth.authStateReady();
    return Promise.resolve(this.auth.currentUser);
  }

  login(email: string, password: string) {
    return signInWithEmailAndPassword(this.auth, email, password);
  }

  async logout() {
    try {
      // Clear client context on logout to ensure clean state for next login
      const { ClientContextService } = await import('./client-context.service');
      const clientContextService = inject(ClientContextService);
      clientContextService.clearOnLogout();
    } catch (error) {
      console.warn('Failed to clear client context on logout:', error);
    }
    
    return this.auth.signOut();
  }

  async resetPassword(email: string): Promise<void> {
    return sendPasswordResetEmail(this.auth, email);
  }

  async confirmPasswordReset(code: string, newPassword: string): Promise<void> {
    return confirmPasswordReset(this.auth, code, newPassword);
  }

  async getUserInfo(uid: string): Promise<User | null> {
    const userRef = doc(this.db, 'users', uid);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      return null;
    }

    return userDoc.data() as User;
  }


  async setCustomClaims(email: string, claims: Record<string, unknown>): Promise<HttpsCallableResult<unknown>> {
    const setCustomClaims = httpsCallable(this.firebaseService.functions, 'setCustomClaims');

    return setCustomClaims({ email, claims });
  }


  ngOnDestroy(): void {
    this.unsubscribe?.();
  }
}
